import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Bell, BellOff, Check, Save, Pencil } from "lucide-react";
import { API, WEEKDAY_SHORT } from "./constants";

// Email alerts management: list, create, edit, pause and delete reminders.
export function AlertsSection({ alerts, reload, schedule }) {
  const EMPTY_FORM = { title: "", message: "", time_of_day: "07:00", weekdays: [1, 2, 3, 4, 5], enabled: true, linked_vinculo_key: null, linked_practice_title: null };
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  // Practices of the month being viewed, grouped by vínculo (for linking).
  const practiceOptions = (schedule?.vinculos || []).flatMap((v) =>
    (v.practices || []).map((p) => ({ vinculo_key: v.key, vinculo_label: v.label, title: p.title }))
  );
  const linkedValue = form.linked_vinculo_key && form.linked_practice_title
    ? `${form.linked_vinculo_key}||${form.linked_practice_title}` : "";
  const setLinked = (val) => {
    if (!val) { setForm((f) => ({ ...f, linked_vinculo_key: null, linked_practice_title: null })); return; }
    const [k, ...rest] = val.split("||");
    setForm((f) => ({ ...f, linked_vinculo_key: k, linked_practice_title: rest.join("||") }));
  };
  const toggleWeekday = (d) => {
    setForm((f) => ({ ...f, weekdays: f.weekdays.includes(d) ? f.weekdays.filter((x) => x !== d) : [...f.weekdays, d].sort() }));
  };
  const startEdit = (a) => {
    setEditingId(a.id);
    setForm({ title: a.title, message: a.message || "", time_of_day: a.time_of_day, weekdays: [...a.weekdays], enabled: a.enabled, linked_vinculo_key: a.linked_vinculo_key || null, linked_practice_title: a.linked_practice_title || null });
    document.querySelector('[data-testid="spiritual-alert-form"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  const cancelEdit = () => { setEditingId(null); setForm(EMPTY_FORM); };
  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Indique um título."); return; }
    if (!form.weekdays.length) { toast.error("Escolha pelo menos um dia da semana."); return; }
    try {
      if (editingId) {
        await axios.patch(`${API}/members/spiritual/alerts/${editingId}`, form, { withCredentials: true });
        toast.success("Alerta atualizado.");
      } else {
        await axios.post(`${API}/members/spiritual/alerts`, form, { withCredentials: true });
        toast.success("Alerta criado.");
      }
      setEditingId(null);
      setForm(EMPTY_FORM);
      reload();
    } catch { toast.error(editingId ? "Erro a atualizar." : "Erro a criar."); }
  };
  const toggle = async (a) => {
    try {
      await axios.patch(`${API}/members/spiritual/alerts/${a.id}`, { enabled: !a.enabled }, { withCredentials: true });
      reload();
    } catch { toast.error("Erro."); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Apagar alerta "${a.title}"?`)) return;
    try {
      await axios.delete(`${API}/members/spiritual/alerts/${a.id}`, { withCredentials: true });
      if (a.id === editingId) cancelEdit();
      reload();
    } catch { toast.error("Erro."); }
  };
  return (
    <div className="mt-10 bg-white border border-[hsl(var(--border))] p-6 lg:p-8" data-testid="spiritual-alerts">
      <div className="overline"><Bell className="inline h-3 w-3 mr-1" /> Alertas por email</div>
      <p className="mt-2 text-xs text-[hsl(var(--muted-foreground))]">Receba um lembrete por email à hora certa nos dias que escolher.</p>

      {alerts.length > 0 && (
        <ul className="mt-5 space-y-2">
          {alerts.map((a) => (
            <li key={a.id} className={`flex items-center justify-between gap-3 p-3 border ${editingId === a.id ? "border-[hsl(var(--accent))] bg-[hsl(var(--secondary))]" : "border-[hsl(var(--border))]"}`} data-testid={`alert-${a.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{a.title}</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                  {a.time_of_day} · {a.weekdays.map((d) => WEEKDAY_SHORT[d - 1]).join(", ")}
                  {a.linked_practice_title && (
                    <span className="ml-2 inline-flex items-center gap-1 text-[hsl(var(--accent))]" title="O email inclui um botão para marcar esta prática como feita" data-testid={`alert-linked-${a.id}`}>
                      <Check className="h-3 w-3" /> {a.linked_practice_title}
                    </span>
                  )}
                </div>
              </div>
              <button onClick={() => toggle(a)} title={a.enabled ? "Pausar alerta" : "Ativar alerta"} className={`text-[10px] uppercase tracking-[0.16em] px-2 py-1 ${a.enabled ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--muted-foreground))]"}`}>
                {a.enabled ? <Bell className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />}
              </button>
              <button onClick={() => startEdit(a)} title="Editar alerta" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--accent))]" data-testid={`alert-edit-${a.id}`}>
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button onClick={() => remove(a)} title="Apagar alerta" className="text-[hsl(var(--muted-foreground))] hover:text-red-700">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={submit} className="mt-6 grid sm:grid-cols-2 gap-3" data-testid="spiritual-alert-form">
        <div className="sm:col-span-2">
          <label className="block text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1">Título *</label>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex.: Oração matinal"
            className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" data-testid="alert-title" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1">Hora *</label>
          <input type="time" value={form.time_of_day} onChange={(e) => setForm({ ...form, time_of_day: e.target.value })}
            className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" data-testid="alert-time" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1">Dias *</label>
          <div className="flex gap-1 flex-wrap">
            {WEEKDAY_SHORT.map((w, idx) => {
              const d = idx + 1;
              const on = form.weekdays.includes(d);
              return (
                <button key={d} type="button" onClick={() => toggleWeekday(d)}
                  className={`px-2 py-1.5 text-[10px] border ${on ? "bg-[hsl(var(--primary))] text-[hsl(var(--accent))] border-[hsl(var(--primary))]" : "border-[hsl(var(--border))]"}`}
                  data-testid={`alert-weekday-${d}`}>
                  {w}
                </button>
              );
            })}
          </div>
        </div>
        <div className="sm:col-span-2">
          <label className="block text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1">Ligar a uma prática (opcional)</label>
          <select value={linkedValue} onChange={(e) => setLinked(e.target.value)}
            className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" data-testid="alert-linked-practice">
            <option value="">— Nenhuma —</option>
            {practiceOptions.map((o) => (
              <option key={`${o.vinculo_key}||${o.title}`} value={`${o.vinculo_key}||${o.title}`}>
                {o.vinculo_label} · {o.title}
              </option>
            ))}
          </select>
          <p className="mt-1 text-[11px] text-[hsl(var(--muted-foreground))]">Se ligar o alerta a uma prática, o email inclui um botão para marcar o dia como feito com um clique.</p>
        </div>
        <div className="sm:col-span-2">
          <label className="block text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1">Mensagem (opcional)</label>
          <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={2}
            className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm" data-testid="alert-message" />
        </div>
        <div className="sm:col-span-2 flex items-center gap-3">
          <button type="submit" className="inline-flex items-center gap-2 px-4 py-2 bg-[hsl(var(--primary))] text-white text-xs uppercase tracking-[0.2em] hover:bg-[hsl(var(--primary))]/90" data-testid="alert-submit">
            {editingId ? <><Save className="h-3.5 w-3.5" /> Guardar alterações</> : <><Plus className="h-3.5 w-3.5" /> Criar alerta</>}
          </button>
          {editingId && (
            <button type="button" onClick={cancelEdit} className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" data-testid="alert-cancel-edit">
              Cancelar
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
